"use client";

import { PropsWithChildren, useState } from "react";
import { Text } from "@stone-payments/jade";
import { ProductVariants } from "@/app/(store)/_components/product-variants";
import { ProductBreadcrumbs } from "@/app/(store)/_components/product-breadcrumbs";
import { ProductsRelated } from "@/app/(store)/_components/products-related";
import { Image, ProductPrice, Skeleton } from "@/components";
import { type Product } from "@/actions/schemas/products";
import { useEffectOnce } from "@/hooks/use-effect-once";
import { productAnalitica } from "@/app/(store)/_utils/events";
import { useStore } from "@/contexts/store";
import { useProductStore } from "@/stores";
import { ProductShareActions } from "@/app/(store)/_components/product-share-actions";
import { ProductCartActions } from "@/app/(store)/_components/product-cart-actions";
import { useExperiment } from "@/contexts/analitica";
import { ExperimentsFlags } from "@/services/amplitude/experiment";

type Props = {
  product: Product;
  loading?: boolean;
  relatedProducts?: Product[];
};

const Title = ({
  loading,
  children,
}: PropsWithChildren<{ loading: boolean }>) =>
  loading ? (
    <Skeleton height="h-6" width="w-52" />
  ) : (
    <Text
      as="h1"
      className="!text-xl !leading-6 !tracking-[-0.34px] !wrap-break-word xl:!text-2xl xl:!leading-7 xl:!tracking-[-0.456px]"
      weight="semibold"
      id="product-name"
      itemProp="name"
      data-testid="product-name"
    >
      {children}
    </Text>
  );

const Description = ({
  loading,
  children,
}: PropsWithChildren<{ loading: boolean }>) => {
  if (loading)
    return <Skeleton height="h-18" width="w-111" className="mb-0 lg:mb-10" />;

  return children ? (
    <Text
      variant="text-medium"
      color="medium"
      itemProp="description"
      data-testid="product-description"
    >
      {children}
    </Text>
  ) : null;
};

export const ProductDetails = ({
  product: defaultProduct,
  loading = true,
  relatedProducts,
}: Props) => {
  const { setProduct } = useProductStore();
  const { enabledWhatsAppSharing } = useStore();

  const [isLoading, setIsLoading] = useState<boolean>(loading);

  const showCartExperiment = useExperiment(ExperimentsFlags.ShowCart);

  useEffectOnce(() => {
    if (!loading) {
      const metadata = {
        hasWhatsapp: enabledWhatsAppSharing,
        hasImage: !!defaultProduct.images?.length,
        hasVariations:
          Array.isArray(defaultProduct.variants) &&
          defaultProduct.variants.length > 1,
        hasDescription: !!defaultProduct.description,
      };

      productAnalitica.events.view.pageViewed(metadata);
    }

    setProduct(defaultProduct);
    setIsLoading(!defaultProduct?.name);
  });

  const { name, description, images, variants } = defaultProduct || {};

  return (
    <div
      id="product-details"
      data-testid="product-details"
      className="relative mx-auto min-h-[calc(100vh-252px)] w-full max-w-300 flex-col px-5 md:px-10 xl:min-h-[calc(100vh-308px)] xl:px-8"
    >
      <div className="my-5 lg:my-8">
        <ProductBreadcrumbs
          loading={isLoading}
          data-testid="product-breadcrumbs"
        />
      </div>

      <article
        className="mb-8 flex flex-col gap-5 md:flex-row md:gap-8 lg:gap-12"
        aria-labelledby="product-name"
        itemScope
        itemType="https://schema.org/Product"
      >
        <section
          className="w-full max-w-[480px] shrink-0 md:w-[280px] lg:w-[460px]"
          aria-label="Galeria de imagens"
        >
          <Image
            src={images?.[0]?.url}
            alt={name}
            size="large"
            loading={isLoading}
            className="xs:min-w-full max-w-full sm:min-w-[220px]"
            data-testid="product-image"
            itemProp="image"
          />
        </section>

        <div className="flex flex-1 flex-col gap-5 sm:mt-4">
          <section>
            <Title loading={isLoading}>{name}</Title>

            <div className="mt-2">
              <ProductPrice
                layout="large"
                loading={isLoading}
                variants={variants}
                data-testid="product-price"
              />
            </div>
          </section>

          <section aria-label="Informações do produto">
            <Description loading={isLoading}>{description}</Description>
          </section>

          <ProductVariants loading={isLoading} />

          {showCartExperiment.key === "on" ? (
            <section aria-label="Ações">
              <ProductCartActions loading={isLoading} />
            </section>
          ) : (
            <section className="mt-auto" aria-label="Ações">
              <ProductShareActions loading={isLoading} />
            </section>
          )}
        </div>
      </article>

      <ProductsRelated products={relatedProducts} loading={isLoading} />

      <div className="mb-8">
        <ProductBreadcrumbs loading={isLoading} />
      </div>
    </div>
  );
};
